import { useState, useRef, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock, User, AlertCircle } from 'lucide-react'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import { useAuth } from '../contexts/AuthContext'
import { verifyTurnstile } from '../utils/api'

const TURNSTILE_SITE_KEY = '0x4AAAAAACW2xKcOgr7vwU8z'
const TURNSTILE_REFRESH_INTERVAL = 270000 // Refresh 30 seconds before 5-minute expiry

export function LoginPage() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const turnstileRef = useRef<TurnstileInstance>(null)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)

  // Auto-refresh Turnstile token before it expires
  const scheduleTokenRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
    }
    refreshTimerRef.current = setTimeout(() => {
      turnstileRef.current?.reset()
    }, TURNSTILE_REFRESH_INTERVAL)
  }, [])

  const handleTurnstileSuccess = useCallback((token: string) => {
    setTurnstileToken(token)
    scheduleTokenRefresh()
  }, [scheduleTokenRefresh])

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null)
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
    }
    // Auto-refresh on expire
    turnstileRef.current?.reset()
  }, [])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
      }
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!turnstileToken) {
      setError('Por favor, complete a verificação de segurança.')
      return
    }

    setLoading(true)

    try {
      // Verify turnstile first
      const isValid = await verifyTurnstile(turnstileToken, 'login')
      if (!isValid) {
        setError('Verificação de segurança falhou. Por favor, tente novamente.')
        turnstileRef.current?.reset()
        setTurnstileToken(null)
        setLoading(false)
        return
      }

      const success = await login(username, password)

      if (success) {
        navigate('/admin')
      } else {
        setError('Credenciais inválidas')
        turnstileRef.current?.reset()
        setTurnstileToken(null)
      }
    } catch {
      setError('Erro ao fazer login. Por favor, tente novamente.')
      turnstileRef.current?.reset()
      setTurnstileToken(null)
    }

    setLoading(false)
  }

  return (
    <div className="flex-1 flex items-center justify-center p-4 bg-gray-50">
      <div className="w-full max-w-md">
        <div className="card p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-primary-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Área de Administração</h1>
            <p className="text-gray-500 mt-2">Introduza as suas credenciais</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="username" className="label">Utilizador</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="input pl-10"
                  placeholder="admin"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="label">Palavra-passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pl-10"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            <div className="flex justify-center">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={handleTurnstileSuccess}
                onError={() => setTurnstileToken(null)}
                onExpire={handleTurnstileExpire}
                options={{
                  theme: 'light',
                  size: 'normal',
                  refreshExpired: 'auto',
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !turnstileToken}
              className="btn-primary w-full"
            >
              {loading ? 'A entrar...' : 'Entrar'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
